import { TooltipDirective } from '../../../../../auxiliares/tooltip/tooltip.directive';
import { BOTONES } from '../../../../../componentes/botones';
import { Component, OnInit, ViewChild, inject } from '@angular/core';
import { CommonModule, DatePipe } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { API_CRON } from '../../../../../../environments/environment';
import { LoginService } from '../../../../login/login.service';
import { ClienteSelectorComponent } from '../../../../../componentes/selectores/cliente-selector/cliente-selector.component';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { MatIconModule } from '@angular/material/icon';
import {
  IInformeDiarioLuminarias,
  IQueryParam,
  IResumenDatosBase,
} from 'modelos/src';
import * as Highcharts from 'highcharts';
import { subDays } from 'date-fns';
import { ResumenesDatosService } from '../../../../../auxiliares/servicios/http/resumenes-datos.service';
import {
  StatCardComponent,
  StatCardConfig,
} from '../../../../../componentes/stat-card/stat-card.component';
import {
  ChartCardComponent,
  ChartCardConfig,
} from '../../../../../componentes/chart-card/chart-card.component';
import { ChartColors } from '../../../../../auxiliares/servicios/chart-colors.service';
import { CarruselComponent } from '../../../../../auxiliares/carrusel/carrusel.component';
import { DatetimePickerComponent } from '../../../../../auxiliares/datetime-picker/datetime-picker.component';

@Component({
  selector: 'app-informe-diario-luminarias',
  standalone: true,
  imports: [
    TooltipDirective,
    CommonModule,
    DatePipe,
    FormsModule,

    MatProgressSpinnerModule,
    MatIconModule,
    BOTONES,

    StatCardComponent,
    ChartCardComponent,
    CarruselComponent,
    DatetimePickerComponent,
    ClienteSelectorComponent,
  ],
  templateUrl: './informe-diario-luminarias.component.html',
  styleUrl: './informe-diario-luminarias.component.scss',
})
export class InformeDiarioLuminariasComponent implements OnInit {
  private resumenesService = inject(ResumenesDatosService);

  @ViewChild(CarruselComponent) carrusel?: CarruselComponent;

  informes: IResumenDatosBase<'Informe Diario Luminarias'>[] = [];
  indiceActual = 0;
  periodo: 7 | 30 = 7;
  cargando = false;
  fechaISO: string = new Date().toISOString();
  // Cliente cuyos informes se muestran. Default: cliente propio. Se puede elegir
  // un hijo desde el selector para ver/descargar sus informes a propósito.
  idClienteSeleccionado: string | null =
    LoginService.getClientePropio()?._id ?? null;

  kpiCards: StatCardConfig[] = [];
  charts: ChartCardConfig[] = [];

  ngOnInit() {
    this.cargarInformes();
  }

  async cargarInformes() {
    this.cargando = true;
    try {
      // Scopear al cliente elegido en el selector (default: cliente propio).
      // includeChildren queda en false: cada vista muestra solo los informes del
      // cliente seleccionado, sin mezclar los de los hijos sin querer.
      const filtro: Record<string, unknown> = {
        tipo: 'Informe Diario Luminarias',
        agrupacion: 'Cliente',
        agrupacionTiempo: 'Diario',
        periodoInicio: {
          $gte: subDays(new Date(), this.periodo).toISOString(),
        },
      };
      if (this.idClienteSeleccionado) {
        filtro['idCliente'] = this.idClienteSeleccionado;
      }
      const query: IQueryParam = {
        filter: JSON.stringify(filtro),
        sort: 'periodoInicio',
        unlimited: true,
        includeChildren: true,
      };
      const resultado =
        await this.resumenesService.getFiltered<'Informe Diario Luminarias'>(
          query,
        );
      this.informes = resultado.datos;
      this.indiceActual = Math.max(0, this.informes.length - 1);
      this.actualizarVista();
      setTimeout(() => this.carrusel?.goToSlide(this.indiceActual));
    } finally {
      this.cargando = false;
    }
  }

  onIndexChange(idx: number) {
    this.indiceActual = idx;
    this.actualizarVista();
  }

  togglePeriodo(periodo: 7 | 30) {
    this.periodo = periodo;
    this.cargarInformes();
  }

  onClienteSeleccionado(idCliente: string | null) {
    this.idClienteSeleccionado = idCliente;
    this.cargarInformes();
  }

  get informeActual():
    | IResumenDatosBase<'Informe Diario Luminarias'>
    | undefined {
    return this.informes[this.indiceActual];
  }

  descargarInforme() {
    const id = this.informeActual?._id;
    if (!id) return;
    // Navegación directa: el endpoint devuelve text/html y el browser lo
    // renderiza. Evita CORS (no es un fetch) y el bloqueo de popups (va dentro
    // del gesto del click, sin await previo).
    window.open(
      `${API_CRON}/resumenes-datos/${id}/informe-luminarias/html`,
      '_blank',
    );
  }

  async generarInforme() {
    const idCliente =
      this.idClienteSeleccionado ??
      this.informeActual?.idCliente ??
      this.informes[0]?.idCliente;
    if (!idCliente) return;
    window.open(
      `${API_CRON}/informes-luminarias/html?idCliente=${encodeURIComponent(idCliente)}&fecha=${encodeURIComponent(this.fechaISO)}`,
      '_blank',
    );
    // Refrescar la lista para que el informe recién generado aparezca en el carrusel
    await this.cargarInformes();
    const diaTarget = this.fechaISO.slice(0, 10);
    const idx = this.informes.findIndex(
      (inf) => inf.periodoInicio?.slice(0, 10) === diaTarget,
    );
    if (idx >= 0) {
      this.indiceActual = idx;
      this.actualizarVista();
      setTimeout(() => this.carrusel?.goToSlide(idx));
    }
  }

  private actualizarVista() {
    const r = this.informeActual?.resumen as
      | IInformeDiarioLuminarias
      | undefined;
    if (!r) {
      this.kpiCards = [];
      this.charts = [];
      return;
    }

    // Cards: solo lo esencial -> total del parque + indicadores críticos.
    // El detalle (cómo se reparte cada estado) se ve en los gráficos.
    this.kpiCards = [
      {
        title: 'Total luminarias',
        value: r.totalLuminarias ?? 0,
        icon: 'lightbulb',
        color: 'blue',
        formatAsNumber: true,
        numberFormat: '1.0-0',
      },
      {
        title: 'Desconectadas',
        value: r.luminariasDesconectadas ?? 0,
        icon: 'cloud_off',
        color: 'red',
        formatAsNumber: true,
        numberFormat: '1.0-0',
      },
      {
        title: 'Con alertas',
        value: r.luminariasConAlarmas ?? 0,
        icon: 'warning',
        color: 'orange',
        formatAsNumber: true,
        numberFormat: '1.0-0',
      },
      ...((r.luminariasConCortesEnergia ?? 0) > 0
        ? [
            {
              title: 'Con corte (GPE)',
              value: r.luminariasConCortesEnergia,
              icon: 'power_off',
              color: 'red' as const,
              formatAsNumber: true,
              numberFormat: '1.0-0',
            },
          ]
        : []),
    ];

    // Gráficos: reparto de cada dimensión como dona + alertas como columnas.
    this.charts = [
      this.donut('Conectividad', 'wifi', 'blue', [
        {
          name: 'Conectadas',
          y: r.luminariasConectadas ?? 0,
          color: ChartColors.success(),
        },
        {
          name: 'Desconectadas',
          y: r.luminariasDesconectadas ?? 0,
          color: ChartColors.error(),
        },
      ]),
      this.donut('Encendido', 'lightbulb', 'yellow', [
        {
          name: 'Encendidas',
          y: r.luminariasEncendidas ?? 0,
          color: ChartColors.encendido(),
        },
        {
          name: 'Apagadas',
          y: r.luminariasApagadas ?? 0,
          color: ChartColors.apagado(),
        },
        {
          name: 'Sin dato',
          y: r.sinDatoEncendido ?? 0,
          color: ChartColors.errorComunicacion(),
        },
      ]),
      this.donut('Estado operativo', 'check_circle', 'teal', [
        {
          name: 'Operativas',
          y: r.luminariasOperativas ?? 0,
          color: ChartColors.success(),
        },
        {
          name: 'Mantenimiento',
          y: r.luminariasEnMantenimiento ?? 0,
          color: ChartColors.warning(),
        },
        {
          name: 'Sin estado',
          y: r.sinEstado ?? 0,
          color: ChartColors.neutral(),
        },
      ]),
    ];

    if (r.tiposAlarmas && Object.keys(r.tiposAlarmas).length > 0) {
      const categorias = Object.keys(r.tiposAlarmas);
      const valores = Object.values(r.tiposAlarmas);
      const colores = ChartColors.getAllCategorical();

      this.charts.push({
        title: 'Tipos de alerta',
        icon: 'warning',
        color: 'red',
        chartHeight: '260px',
        chartOptions: {
          chart: { type: 'column' },
          xAxis: { categories: categorias },
          yAxis: { title: { text: null }, allowDecimals: false },
          legend: { enabled: false },
          plotOptions: {
            column: { maxPointWidth: 56, borderRadius: 4 },
          },
          series: [
            {
              type: 'column',
              name: 'Luminarias',
              data: valores.map((v, i) => ({
                y: v,
                color: colores[i % colores.length],
              })),
            },
          ] as Highcharts.SeriesOptionsType[],
        },
      });
    }
  }

  /** Construye la config de una dona (pie con hueco) para un reparto de estados. */
  private donut(
    title: string,
    icon: string,
    color: ChartCardConfig['color'],
    data: { name: string; y: number; color: string }[],
  ): ChartCardConfig {
    return {
      title,
      icon,
      color,
      chartHeight: '260px',
      chartOptions: {
        chart: { type: 'pie' },
        tooltip: { pointFormat: '<b>{point.y}</b> ({point.percentage:.0f}%)' },
        plotOptions: {
          pie: {
            innerSize: '62%',
            borderWidth: 0,
            dataLabels: {
              enabled: true,
              format: '{point.y}',
              distance: -22,
            },
            showInLegend: true,
          },
        },
        legend: {
          align: 'center',
          verticalAlign: 'bottom',
          itemMarginTop: 2,
        },
        series: [
          { type: 'pie', name: title, data },
        ] as Highcharts.SeriesOptionsType[],
      },
    };
  }
}
